import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import toast from 'react-hot-toast';
import { addWidget, editContent } from 'src/redux/actions/Content';
import { LIVE_QUALITIES } from 'src/utils/liveWidgets';
import { LIVE_PLATFORMS } from 'src/utils/liveEmbed';

const QUALITY_LABELS = {
  auto: 'Auto',
  '1080p': '1080p',
  '720p': '720p',
  '480p': '480p',
  '360p': '360p',
};

const createLiveDialog = (widgetCat) => {
  const { label, icon, placeholder } = LIVE_PLATFORMS[widgetCat];

  const DialogBoxLive = ({ isOpen, onClose, isIcon, startupData }) => {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.User, shallowEqual);
    const { currentFolder } = useSelector(
      (state) => state.FileFolder,
      shallowEqual,
    );

    const [name, setName] = useState('');
    const [url, setUrl] = useState('');
    const [mute, setMute] = useState(true);
    const [quality, setQuality] = useState('auto');
    const [saving, setSaving] = useState(false);

    const isEdit = Boolean(startupData);

    useEffect(() => {
      if (!isOpen) return;
      if (startupData) {
        const w = startupData.widget || {};
        setName(startupData.name || '');
        setUrl(w.url || '');
        setMute(Boolean(w.mute));
        setQuality(w.quality || 'auto');
      } else {
        setName('');
        setUrl('');
        setMute(true);
        setQuality('auto');
      }
    }, [isOpen, startupData]);

    const canSave = name.trim() && url.trim();

    const handleSave = async () => {
      setSaving(true);
      try {
        const widgetData = {
          name: name.trim(),
          type: 'widget',
          widgetCat,
          widgetIcon: isIcon || icon,
          url: url.trim(),
          mute,
          quality,
          user: user.isMember ? user?.user._id : user._id,
          folderId: currentFolder,
          origin: 'contentWidget',
        };
        const result = isEdit
          ? await dispatch(editContent({ object: widgetData, id: startupData.id }))
          : await dispatch(addWidget(widgetData));
        if (result) {
          onClose(false);
        }
      } catch (error) {
        toast.error(error.message || `Failed to save ${label}`);
      } finally {
        setSaving(false);
      }
    };

    return (
      <Dialog fullWidth maxWidth='sm' open={Boolean(isOpen)} onClose={() => onClose(false)}>
        <Box display='flex' flexDirection='column' alignItems='center' sx={{ pt: 3 }}>
          <img src={icon} alt={label} width={60} />
        </Box>
        <DialogTitle sx={{ textAlign: 'center', fontWeight: 'bold' }}>
          {label}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label='Name *'
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
            />
            <TextField
              label='Live stream URL *'
              placeholder={placeholder}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel id={`${widgetCat}-quality`}>Stream quality</InputLabel>
              <Select
                labelId={`${widgetCat}-quality`}
                label='Stream quality'
                value={quality}
                onChange={(e) => setQuality(e.target.value)}
              >
                {LIVE_QUALITIES.map((q) => (
                  <MenuItem key={q} value={q}>
                    {QUALITY_LABELS[q]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch checked={mute} onChange={(e) => setMute(e.target.checked)} />
              }
              label={mute ? 'Mute: on' : 'Mute: off'}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button variant='outlined' onClick={() => onClose(false)}>
            Close
          </Button>
          <Button variant='contained' disabled={!canSave || saving} onClick={handleSave}>
            {saving ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  DialogBoxLive.displayName = `DialogBoxLive_${widgetCat}`;
  return DialogBoxLive;
};

export const DialogBoxLiveYoutube = createLiveDialog('live_youtube');
export const DialogBoxLiveTwitch = createLiveDialog('live_twitch');
export const DialogBoxLiveVimeo = createLiveDialog('live_vimeo');
